/**
 * Compatibility facade for the retired browser-owned media timeline.
 *
 * Product and service publication runs through itemPublishingAuthority. Media
 * posts use the bounded media authority callables; this facade keeps the
 * existing composer/store contract stable while ensuring the browser never
 * receives a Firestore/Storage writer.
 */

import { mediaAuthorityService } from "@/services/mediaAuthorityService";

const MEDIA_CREATION_OPERATION_ID_PATTERN = /^[A-Za-z0-9_-]{16,128}$/;

export function buildFinalizePublishedPostPayload({
  operationId,
  postId,
  timelineDocument,
}) {
  return Object.freeze({
    operationId,
    postId,
    organisationId: timelineDocument.organisation,
    contentKind: "mediaPost",
    mediaKind: timelineDocument.mediaType,
    title: timelineDocument.title,
    text: timelineDocument.caption,
  });
}

export function mediaTimelineDocIdForOperation(operationId) {
  if (
    typeof operationId !== "string" ||
    !MEDIA_CREATION_OPERATION_ID_PATTERN.test(operationId)
  ) {
    throw new TypeError("A valid media creation operation ID is required");
  }
  return `media_${operationId}`;
}

export async function createMedia(payload, { operationId } = {}) {
  if (operationId !== undefined) mediaTimelineDocIdForOperation(operationId);
  if (typeof operationId !== "string") {
    throw new TypeError("A media creation operation ID is required");
  }
  const result = await mediaAuthorityService.createMediaPost(payload, operationId);
  return { success: true, id: result.id, revision: result.revision || 1 };
}

export async function updateMedia(id, payload, { operationId, expectedRevision = 1 } = {}) {
  const operation = operationId || `update_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  let revision = expectedRevision;
  if (revision === 1 && !Number.isSafeInteger(payload?.revision)) {
    try {
      revision = (await getById(id))?.revision || 1;
    } catch {
      revision = 1;
    }
  }
  const result = await mediaAuthorityService.updateMediaPost(id, payload, operation, revision);
  return { success: true, id: result.id, revision: result.revision };
}

export async function deleteMedia(id, { operationId, expectedRevision = 1 } = {}) {
  const operation = operationId || `delete_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  let revision = expectedRevision;
  if (revision === 1) {
    try {
      revision = (await getById(id))?.revision || 1;
    } catch {
      revision = 1;
    }
  }
  const result = await mediaAuthorityService.deleteMediaPost(id, operation, revision);
  return { success: true, id: result.id };
}

export async function getById(id) {
  try {
    return await mediaAuthorityService.getMediaPost(id);
  } catch (error) {
    if (error?.code === "not-found") return null;
    throw error;
  }
}

export async function fetchPage(options = {}) {
  const { fetchMarketplaceItems } = await import("./timelineFetcher");
  return fetchMarketplaceItems(options);
}
